// pages/formularios/EnviarFormulario.js — "Enviar formulário", aberto pelo chat.
//
// Out/26, Formulários PR 2. O agente escolhe um formulário publicado do chip
// da conversa, ajusta a mensagem e o texto do botão, e vê à direita o que o
// cliente recebe: a bolha com o botão e, ao tocar, as telas do formulário
// (a mesma prévia do construtor).
//
// Formulário pelo chat é mensagem interativa: a Meta só entrega com a
// janela de 24h aberta. A conta da janela feita aqui (`janelaAberta`) é só
// pra avisar antes; quem decide é o servidor, que devolve 409 com a frase.
//
// PR 3: com a janela fechada, o diálogo troca a mensagem livre pelos
// templates APROVADOS cujo botão abre ESTE formulário (o vínculo é feito na
// criação do template e não muda — template sem esse botão não levaria o
// formulário, só reabriria a conversa). Sem nenhum, oferece criar um. O
// envio vai pelo mesmo caminho do painel de templates do chat (variáveis,
// crédito, teto), e o servidor põe o token do formulário no botão.
//
// Cores do chat (`--ch-*`, em :root no ChatPage.css) no diálogo; a prévia do
// formulário usa as `--fm-*` (Formularios.css), que seguem o mesmo tema.

import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { FiAlertTriangle, FiClipboard, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../services/Api';
import PreviaFlow from './PreviaFlow';
import './Formularios.css';

const SYNE = "'Syne', sans-serif";
const TEXTO_MAX = 1024;
const CTA_MAX = 20;
const PADRAO = { texto: 'Para agilizar o seu atendimento, responda este formulário rapidinho:', cta: 'Responder' };
const CHAVE = 'cetto:formulario-envio';
const EMOJI = /\p{Extended_Pictographic}/u;

const ch = (n) => `var(--ch-${n})`;

// O tamanho da letra vai no <input>, não na raiz: na raiz ele encolhe também
// o recorte da borda onde o rótulo assenta, e a linha passa por cima dele.
const campo = {
  '& .MuiInputBase-root': { color: ch('text'), bgcolor: ch('surf') },
  '& .MuiInputBase-input': { fontSize: '0.86rem' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: ch('bdr') },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: ch('bdr2') },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ch('green') },
  '& .MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: ch('red') },
  '& .MuiInputLabel-root': { color: ch('text2') },
  '& .MuiInputLabel-root.Mui-focused': { color: ch('green-d') },
  '& .MuiFormHelperText-root': { color: ch('text3'), mx: 0.5 },
  '& .MuiFormHelperText-root.Mui-error': { color: ch('red') },
};

const rotulo = {
  fontFamily: SYNE, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.12em',
  textTransform: 'uppercase', color: ch('text3'), mb: 1,
};

function lembrado() {
  try { return { ...PADRAO, ...JSON.parse(localStorage.getItem(CHAVE) || '{}') }; } catch { return PADRAO; }
}

// O botão FLOW do template (vem da Meta com os componentes), ou null.
function botaoDoTemplate(tpl) {
  for (const c of tpl?.components || []) {
    if ((c?.type || '').toUpperCase() !== 'BUTTONS') continue;
    const b = (c.buttons || []).find((x) => (x?.type || '').toUpperCase() === 'FLOW');
    if (b) return b;
  }
  return null;
}

function corpoDoTemplate(tpl) {
  const c = (tpl?.components || []).find((x) => (x?.type || '').toUpperCase() === 'BODY');
  return c?.text || tpl?.preview || '';
}

function erroDaApi(err, padrao) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return d?.mensagem || padrao;
}

// A mensagem como chega no WhatsApp do cliente: texto e o botão do formulário.
function BolhaDoCliente({ texto, cta }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'var(--fm-wa-wall)', backgroundImage: 'radial-gradient(var(--fm-wa-dot) 1px, transparent 1px)', backgroundSize: '14px 14px' }}>
      <Box sx={{ maxWidth: 260, bgcolor: 'var(--fm-wa-card)', borderRadius: '10px', borderTopLeftRadius: '3px', boxShadow: '0 1px 1px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
        <Typography sx={{ px: 1.25, pt: 1, pb: 0.75, fontSize: '0.82rem', color: 'var(--fm-wa-ink)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>
          {texto.trim() || 'Mensagem'}
        </Typography>
        <Box sx={{ borderTop: '1px solid var(--fm-wa-line)', py: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, color: '#027eb5', fontSize: '0.84rem', fontWeight: 600 }}>
          <FiClipboard size={14} /> {cta.trim() || 'Botão'}
        </Box>
      </Box>
    </Box>
  );
}

export default function EnviarFormulario({
  open, onClose, deviceId, client, janelaAberta = true, onEnviado,
  templates = [], onEnviarTemplate, podeCriarTemplate = false,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const celular = useMediaQuery(theme.breakpoints.down('sm'));
  const [lista, setLista] = useState(null);
  const [erroLista, setErroLista] = useState('');
  const [sel, setSel] = useState(null);
  const [previas, setPrevias] = useState({});
  const [tela, setTela] = useState(0);
  const [texto, setTexto] = useState(() => lembrado().texto);
  const [cta, setCta] = useState(() => lembrado().cta);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [tplSel, setTplSel] = useState(null);
  const porTemplate = !janelaAberta;

  useEffect(() => {
    if (!open || !deviceId) return;
    setErro(''); setErroLista(''); setLista(null); setTela(0);
    api.get('/formularios/para-enviar', { params: { device_id: deviceId } })
      .then((r) => {
        const itens = r.data?.items || [];
        setLista(itens);
        setSel((atual) => (itens.some((f) => f.id === atual) ? atual : itens[0]?.id || null));
      })
      .catch((err) => { setLista([]); setErroLista(erroDaApi(err, 'Não foi possível carregar os formulários.')); });
  }, [open, deviceId]);

  useEffect(() => {
    if (!open || !sel || previas[sel]) return;
    api.get(`/formularios/${sel}/previa`)
      .then((r) => setPrevias((p) => ({ ...p, [sel]: r.data.definition })))
      .catch(() => setPrevias((p) => ({ ...p, [sel]: { telas: [] } })));
  }, [open, sel, previas]);

  const escolhido = useMemo(() => (lista || []).find((f) => f.id === sel), [lista, sel]);
  const templatesDoFormulario = useMemo(() => (
    escolhido?.meta_flow_id
      ? (templates || []).filter((t) => String(botaoDoTemplate(t)?.flow_id || '') === String(escolhido.meta_flow_id))
      : []
  ), [templates, escolhido]);
  const template = templatesDoFormulario.find((t) => t.name === tplSel) || templatesDoFormulario[0] || null;
  const problemaTexto = !texto.trim() ? 'Escreva a mensagem.' : texto.length > TEXTO_MAX ? `Passa de ${TEXTO_MAX} caracteres.` : '';
  const problemaCta = !cta.trim() ? 'Escreva o texto do botão.'
    : cta.trim().length > CTA_MAX ? `Até ${CTA_MAX} caracteres.`
      : EMOJI.test(cta) ? 'Sem emoji: o WhatsApp não aceita emoji no botão.' : '';
  const podeEnviar = Boolean(escolhido) && !problemaTexto && !problemaCta && !enviando;

  const enviar = async () => {
    if (!podeEnviar) return;
    setEnviando(true); setErro('');
    try {
      await api.post(`/formularios/${escolhido.id}/enviar`, {
        device_id: deviceId, client_id: client.id, texto: texto.trim(), cta: cta.trim(),
      });
      try { localStorage.setItem(CHAVE, JSON.stringify({ texto: texto.trim(), cta: cta.trim() })); } catch { /* sem storage */ }
      onEnviado?.(escolhido);
    } catch (err) {
      setErro(erroDaApi(err, 'Não foi possível enviar o formulário.'));
    } finally {
      setEnviando(false);
    }
  };

  const enviarTemplate = async () => {
    if (!template || !onEnviarTemplate) return;
    setEnviando(true); setErro('');
    try {
      await onEnviarTemplate(template);
    } finally {
      setEnviando(false);
    }
  };

  const criarTemplate = () => {
    if (!escolhido) return;
    const qs = new URLSearchParams({ aba: 'criar', formulario: escolhido.id, chip: deviceId, nome: escolhido.name });
    navigate(`/admin/meta?${qs.toString()}`);
  };

  const nomeContato = (client?.name || '').trim() || 'este contato';
  // A bolha da prévia: a mensagem livre, ou o corpo e o botão do template.
  const previaTexto = porTemplate ? corpoDoTemplate(template) : texto;
  const previaBotao = porTemplate ? (botaoDoTemplate(template)?.text || 'Abrir formulário') : cta;

  return (
    <Dialog
      open={open} onClose={enviando ? undefined : onClose} fullScreen={celular} maxWidth="md" fullWidth
      aria-labelledby="enviar-formulario-titulo"
      PaperProps={{ sx: { bgcolor: ch('card'), color: ch('text'), backgroundImage: 'none', border: celular ? 'none' : `1px solid ${ch('bdr')}`, borderRadius: celular ? 0 : '14px' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.75, borderBottom: `1px solid ${ch('bdr')}`, bgcolor: ch('surf') }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: ch('blue-bg'), color: ch('blue'), border: `1px solid ${ch('blue-b')}` }}>
          <FiClipboard size={15} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="enviar-formulario-titulo" sx={{ fontFamily: SYNE, fontWeight: 800, fontSize: '1rem', color: ch('text') }}>Enviar formulário</Typography>
          <Typography sx={{ fontSize: '0.74rem', color: ch('text2'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>para {nomeContato}</Typography>
        </Box>
        <IconButton aria-label="Fechar" onClick={onClose} disabled={enviando} sx={{ color: ch('text3') }}><FiX size={17} /></IconButton>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 300px' }, overflowY: 'auto' }}>
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {!janelaAberta && (
            <Box role="note" sx={{ display: 'flex', gap: 1, p: 1.5, borderRadius: '10px', bgcolor: ch('amber-bg'), border: `1px solid ${ch('amber-b')}`, color: ch('amber-d'), fontSize: '0.8rem', lineHeight: 1.45 }}>
              <FiAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>O cliente não escreveu nas últimas 24 horas. Fora dessa janela, o formulário vai num template aprovado com o botão dele — a Meta cobra o template.</span>
            </Box>
          )}

          <Box>
            <Typography sx={rotulo}>Formulário</Typography>
            {lista === null ? (
              <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={20} sx={{ color: ch('green') }} /></Box>
            ) : erroLista ? (
              <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.8rem' }}>{erroLista}</Alert>
            ) : !lista.length ? (
              <Box sx={{ border: `1px dashed ${ch('bdr2')}`, borderRadius: '12px', p: 2.5, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: SYNE, fontWeight: 700, fontSize: '0.9rem', color: ch('text') }}>Nenhum formulário publicado para este chip</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: ch('text2'), mt: 0.5 }}>Supervisores montam e publicam formulários em Gerenciamento → Formulários.</Typography>
              </Box>
            ) : (
              <Box role="radiogroup" aria-label="Formulários publicados" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {lista.map((f) => {
                  const ativo = f.id === sel;
                  return (
                    <Box
                      key={f.id} component="button" type="button" role="radio" aria-checked={ativo}
                      onClick={() => { setSel(f.id); setTela(0); }}
                      sx={{
                        textAlign: 'left', font: 'inherit', cursor: 'pointer', p: '11px 13px', borderRadius: '10px',
                        bgcolor: ativo ? ch('blue-bg') : ch('surf'), color: ch('text'),
                        border: `1px solid ${ativo ? ch('blue') : ch('bdr')}`, transition: 'border-color .15s, background-color .15s',
                        '&:hover': { borderColor: ativo ? ch('blue') : ch('bdr2') },
                        '&:focus-visible': { outline: `2px solid ${ch('blue')}`, outlineOffset: 2 },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily: SYNE, fontWeight: 700, fontSize: '0.86rem', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{f.name}</Typography>
                        {f.status === 'THROTTLED' && <Box component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, color: ch('amber-d'), bgcolor: ch('amber-bg'), px: 0.75, py: '1px', borderRadius: '5px' }}>Limitado</Box>}
                      </Box>
                      <Typography sx={{ fontSize: '0.74rem', color: ch('text2'), mt: 0.4, wordBreak: 'break-word' }}>{(f.roteiro || []).join(' → ')}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: ch('text3'), mt: 0.4 }}>
                        {f.campos} pergunta{f.campos === 1 ? '' : 's'}{f.pasta ? ` · quem responder entra na pasta ${f.pasta}` : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {porTemplate ? (escolhido && (
            <Box>
              <Typography sx={rotulo}>Template com este formulário</Typography>
              {!templatesDoFormulario.length ? (
                <Box sx={{ border: `1px dashed ${ch('bdr2')}`, borderRadius: '12px', p: 2.5, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: SYNE, fontWeight: 700, fontSize: '0.9rem', color: ch('text') }}>
                    Nenhum template aprovado abre “{escolhido.name}”
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: ch('text2'), mt: 0.5, mb: podeCriarTemplate ? 1.5 : 0 }}>
                    {podeCriarTemplate
                      ? 'Crie um template com o botão deste formulário. Depois que a Meta aprovar, ele aparece aqui.'
                      : 'Peça a um supervisor para criar um template com o botão deste formulário.'}
                  </Typography>
                  {podeCriarTemplate && (
                    <Button size="small" onClick={criarTemplate} startIcon={<FiClipboard size={13} />}
                      sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: ch('green-d') }}>
                      Criar template com este formulário
                    </Button>
                  )}
                </Box>
              ) : (
                <Box role="radiogroup" aria-label="Templates com este formulário" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {templatesDoFormulario.map((t) => {
                    const ativo = t.name === template?.name;
                    return (
                      <Box
                        key={`${t.name}-${t.language}`} component="button" type="button" role="radio" aria-checked={ativo}
                        onClick={() => setTplSel(t.name)}
                        sx={{
                          textAlign: 'left', font: 'inherit', cursor: 'pointer', p: '11px 13px', borderRadius: '10px',
                          bgcolor: ativo ? ch('purple-bg') : ch('surf'), color: ch('text'),
                          border: `1px solid ${ativo ? ch('purple') : ch('bdr')}`,
                          '&:focus-visible': { outline: `2px solid ${ch('purple')}`, outlineOffset: 2 },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontFamily: SYNE, fontWeight: 700, fontSize: '0.84rem', flex: 1, minWidth: 0, wordBreak: 'break-all' }}>{t.label || t.name}</Typography>
                          <Box component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, color: ch('text2'), bgcolor: ch('inset'), px: 0.75, py: '1px', borderRadius: '5px' }}>
                            {t.category === 'UTILITY' ? 'Utilidade' : 'Marketing'}
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: '0.74rem', color: ch('text2'), mt: 0.4, wordBreak: 'break-word' }}>
                          {(t.preview || corpoDoTemplate(t)).slice(0, 140)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )) : (
            <>
              <TextField
                label="Mensagem" multiline minRows={3} maxRows={8} value={texto} onChange={(e) => setTexto(e.target.value)}
                error={Boolean(problemaTexto) && texto.length > 0}
                helperText={(texto.length > 0 && problemaTexto) || `${texto.length}/${TEXTO_MAX} · vai junto do botão do formulário.`}
                sx={campo} fullWidth
              />
              <TextField
                label="Texto do botão" value={cta} onChange={(e) => setCta(e.target.value)} size="small"
                error={Boolean(problemaCta)}
                helperText={problemaCta || `${cta.trim().length}/${CTA_MAX} · é o botão que abre o formulário.`}
                sx={{ ...campo, maxWidth: 320 }} fullWidth
              />
            </>
          )}
          {erro && <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.8rem' }}>{erro}</Alert>}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={rotulo}>Como o cliente recebe</Typography>
          <BolhaDoCliente texto={previaTexto} cta={previaBotao} />
          {escolhido && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ ...rotulo, mb: 0.75 }}>Ao tocar em “{(previaBotao || '').trim() || 'Botão'}”</Typography>
              {previas[sel]
                ? (previas[sel].telas?.length
                  ? <PreviaFlow definicao={previas[sel]} indice={tela} onTrocar={setTela} />
                  : <Typography sx={{ fontSize: '0.76rem', color: ch('text3') }}>Prévia indisponível.</Typography>)
                : <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={18} sx={{ color: ch('green') }} /></Box>}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', px: 2.5, py: 1.5, borderTop: `1px solid ${ch('bdr')}`, bgcolor: ch('surf'), mt: 'auto' }}>
        <Button onClick={onClose} disabled={enviando} sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: ch('text2') }}>Cancelar</Button>
        <Button
          variant="contained" disableElevation
          onClick={porTemplate ? enviarTemplate : enviar}
          disabled={porTemplate ? (!template || enviando) : !podeEnviar}
          startIcon={enviando ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <FiClipboard size={14} />}
          sx={{
            textTransform: 'none', fontFamily: SYNE, fontWeight: 700, borderRadius: '8px', px: 2.25,
            bgcolor: ch('green'), color: '#fff', '&:hover': { bgcolor: ch('green-d') },
            '&.Mui-disabled': { bgcolor: ch('inset'), color: ch('text3') },
          }}
        >
          {enviando ? 'Enviando…' : porTemplate ? 'Enviar template' : 'Enviar formulário'}
        </Button>
      </Box>
    </Dialog>
  );
}
